/**
 * Landings entered as RANGES — three questions use it, and one of them is about
 * a material.
 *
 * Specification Q11 and the catalog hint are explicit about why: a 20-landing
 * consultant job must not cost the surveyor 19 taps. "2–20 sheetrock" is one
 * row. The control is add / edit / remove over `WallRange[]`.
 *
 * ── Two modes, because Amendment 5 gave range entry a second job ─────────────
 *
 *   **L4a** — wall type per landing. The material IS the answer: it selects the
 *   per-landing cut-and-patch price, so every row carries a picker.
 *
 *   **F11a / F12a** (`landingsOnly`) — the landings missing a smoke detector,
 *   and the landings that are sprinklered. These are spans of landings for the
 *   fire alarm company and nothing else. There is no material, so there is no
 *   picker: `WallRange.wallType` is optional for exactly this reason (types.ts),
 *   and a row here is written WITHOUT one. Absent means "this row is not about a
 *   material" — writing a wall type nobody chose would put invented survey data
 *   on the record, and offering a control that means nothing would be one more
 *   thing to dismiss on a 430px screen.
 *
 * The mode is decided by the caller (`QuestionField`), never inferred here.
 *
 * Two things this component deliberately does NOT do:
 *
 *  1. It does not decide the rules. Round 7 (Stephanie): ranges must not
 *     overlap, must not repeat past what the group's last landing leaves free,
 *     and typing into a bound must not fight the keystroke. Those are
 *     judgements about the document, so they live in `landingRanges.ts`
 *     (`freeSpans`, `seedRange`, `canAddRange`, `commitRange`) — a second rule
 *     engine belongs in `src/ui/state`, not folded into the `.tsx` that draws
 *     the boxes. This component only calls those functions and renders what
 *     they decide: `add()` seeds from `seedRange`, the `+` button renders only
 *     when `canAddRange` is true (D-015 — dead, so absent, not disabled), and
 *     every edit goes through `commitRange`, which clamps to `[1, maxLanding]`
 *     and clips to the free space between a row's neighbours before it writes.
 *  2. It does not own the wall-type list. Those labels come from the catalog's
 *     L2 options via `wallTypeOptions()`, so the Main Egress picker and the
 *     per-landing picker can never drift apart.
 *
 * The remove control is a full-width labelled button, not a 12px "×" in a
 * corner (brief §2): every target here is at least 44x44.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';

import { byId, visibleOptions } from '../../core/catalog';
import type { FormSet, WallRange, WallType } from '../../core/types';
import { canAddRange, commitRange, seedRange, type Span } from '../state/landingRanges';

/**
 * The wall materials, taken from catalog question L2 (Main Egress wall type).
 * `WallType` in types.ts and the L2 option list are the same materials — seven
 * of them since Amendment 5 added Stucco. This reads the catalog rather than
 * restating them, which is exactly why that amendment was a one-line change:
 * adding Stucco to L2 put it in the L4a per-landing picker at the same time.
 */
export function wallTypeOptions(formSet: FormSet): { value: WallType; label: string }[] {
  const question = byId('L2');
  if (!question) return [];
  return visibleOptions(question, formSet).map((option) => ({
    value: option.value as WallType,
    label: option.label,
  }));
}

const FALLBACK_WALL_TYPE: WallType = 'sheetrock';

export interface RangeFieldProps {
  value: readonly WallRange[];
  onChange: (next: WallRange[]) => void;
  formSet: FormSet;
  /**
   * The group's landing count, from the L1 derived line. Used only as the
   * stepper ceiling and to seed a new row; absent means "no ceiling known".
   */
  maxLanding?: number;
  /** Accessible name for the whole editor — the question label. */
  ariaLabel: string;
  disabled?: boolean;
  /**
   * Rows are spans of landings and nothing else — no material, no picker, and
   * no `wallType` written. F11a and F12a; see the header.
   */
  landingsOnly?: boolean;
}

export function RangeField({
  value,
  onChange,
  formSet,
  maxLanding,
  ariaLabel,
  disabled = false,
  landingsOnly = false,
}: RangeFieldProps): ReactNode {
  const id = useId();
  const options = wallTypeOptions(formSet);
  const ranges = [...value];

  // Every landing-bound edit goes through `commitRange` (landingRanges.ts):
  // clamp to [1, maxLanding], drag the other bound, then clip to the free
  // space between this row's neighbours — the no-overlap rule, in one place.
  // `commitRange` returns `ranges` itself, unchanged, when the edit resolves
  // to nothing new (refused, or clamped/clipped back to what was already
  // there) — skip `onChange` in exactly that case, so a no-op edit is not a
  // write.
  const commit = (index: number, patch: Partial<Span>): void => {
    const next = commitRange(ranges, index, patch, maxLanding);
    if (next !== ranges) onChange(next);
  };

  // Wall type isn't a landing bound, so it bypasses commitRange entirely —
  // there is no overlap or ceiling rule for a material.
  const setWallType = (index: number, wallType: WallType): void => {
    const current = ranges[index];
    if (!current) return;
    const next = [...ranges];
    next[index] = { ...current, wallType };
    onChange(next);
  };

  const add = (): void => {
    const seed = seedRange(ranges, maxLanding);
    // The `+` button only renders when `canAddRange` is true, but guard here
    // too rather than trust the render below never to drift from this call.
    if (!seed) return;
    const row: WallRange = { ...seed };
    // A landings-only row is written without a material, deliberately: see the
    // header. Carrying `wallType: 'sheetrock'` because the type once required
    // one would be a claim about a wall nobody looked at.
    if (!landingsOnly) {
      row.wallType = ranges[ranges.length - 1]?.wallType ?? options[0]?.value ?? FALLBACK_WALL_TYPE;
    }
    // Splice the new row into landing order rather than always appending —
    // `commitRange` trusts array neighbours to be spatial neighbours, and a
    // seed can land in a gap BEFORE the last existing row.
    const insertAt = ranges.findIndex((existing) => existing.fromLanding > seed.fromLanding);
    const next =
      insertAt === -1
        ? [...ranges, row]
        : [...ranges.slice(0, insertAt), row, ...ranges.slice(insertAt)];
    onChange(next);
  };

  const remove = (index: number): void => {
    onChange(ranges.filter((_, at) => at !== index));
  };

  return (
    <div className="ranges" role="group" aria-label={ariaLabel}>
      {/*
       * How range entry works, said ABOVE the rows and the + button and said
       * ALWAYS — not only while the list happens to be empty. A 20-landing job
       * is where this control earns its keep, and that is exactly the moment
       * the empty-state copy has already scrolled away (standard item 6).
       */}
      <p className="ranges__lead" data-legibility="helper">
        {landingsOnly
          ? 'One row per stretch of landings — “3–7” is a single row, not five. Set the ' +
            'landings it covers and add another row for the next stretch. Nothing else to pick.'
          : 'One row per stretch of the same material — “2–20 sheetrock” is a single row, not ' +
            'nineteen. Set the landings it covers, pick the wall type, add another row for the ' +
            'next stretch.'}
      </p>

      {ranges.length === 0 ? (
        <p className="ranges__empty" data-legibility="helper">
          No landing ranges yet. Tap <b>+ Add landing range</b> to start the first one.
        </p>
      ) : null}

      {ranges.map((range, index) => {
        const rowId = `${id}-${index}`;
        const label = `Landings ${range.fromLanding}–${range.toLanding}`;
        return (
          <div className="range" key={rowId}>
            <div className="range__bounds">
              <label className="range__cap" htmlFor={`${rowId}-from`}>
                From
              </label>
              <LandingInput
                id={`${rowId}-from`}
                value={range.fromLanding}
                bound="fromLanding"
                maxLanding={maxLanding}
                disabled={disabled}
                onCommit={(patch) => commit(index, patch)}
              />
              <span className="range__dash" aria-hidden="true">
                –
              </span>
              <label className="range__cap" htmlFor={`${rowId}-to`}>
                To
              </label>
              <LandingInput
                id={`${rowId}-to`}
                value={range.toLanding}
                bound="toLanding"
                maxLanding={maxLanding}
                disabled={disabled}
                onCommit={(patch) => commit(index, patch)}
              />
            </div>

            {landingsOnly ? null : (
              <>
                <label className="range__cap" htmlFor={`${rowId}-type`}>
                  Wall type
                </label>
                {/* Native <select>: iOS raises its own wheel, which is a far
                 * better one-thumb target than anything drawn here. */}
                <select
                  id={`${rowId}-type`}
                  className="field__input range__type"
                  // A row stored before `wallType` became optional always has
                  // one; the fallback keeps the control from going uncontrolled
                  // if a landings-only row is ever rendered in material mode.
                  value={range.wallType ?? options[0]?.value ?? FALLBACK_WALL_TYPE}
                  disabled={disabled}
                  onChange={(event) => setWallType(index, event.target.value as WallType)}
                >
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            <button
              type="button"
              className="btn btn--ghost range__remove"
              disabled={disabled}
              onClick={() => remove(index)}
            >
              Remove {label}
            </button>
          </div>
        );
      })}

      {/*
       * D-015: a dead control is a puzzle. Once `canAddRange` is false — every
       * landing 1..maxLanding already sits inside a row — the button is GONE,
       * not greyed out. This is Stephanie's "I can keep adding landing ranges
       * even when the values are the same" fixed at its source: there is
       * nothing left to press.
       */}
      {canAddRange(ranges, maxLanding) ? (
        <button type="button" className="btn btn--ghost ranges__add" disabled={disabled} onClick={add}>
          + Add landing range
        </button>
      ) : null}
    </div>
  );
}

interface LandingInputProps {
  id: string;
  value: number;
  bound: 'fromLanding' | 'toLanding';
  maxLanding?: number;
  disabled: boolean;
  onCommit: (patch: Partial<Span>) => void;
}

/**
 * One landing-bound number box — the draft pattern from `TextField.tsx`
 * (`useDraft`, D-037), specialised to a STRING draft over a numeric value.
 *
 * The bug this replaces: the old input was controlled straight onto the
 * stored number, clamped on every keystroke (`clampLanding`). Clearing it
 * snapped the field to 1 before the next character landed, so typing "5"
 * after a clear became "15" — and since every write persists before it is
 * visible (`SurveyProvider` rule 1, D-037 §5), a still-in-flight round trip
 * could put the field back mid-keystroke on top of that.
 *
 * So while focused this shows the DRAFT, not the stored value, and only
 * touches the document on blur or Enter. An empty or non-numeric draft is
 * simply never committed — the effect below reverts the field to the stored
 * value the moment focus leaves, exactly like a cancelled edit.
 *
 * `commit()` also skips `onCommit` when the parsed draft is exactly the
 * value already stored — tapping into a field and tapping away with no edit
 * must not persist a write, and Enter must not persist two: it fires `blur()`
 * rather than committing directly AND on the blur that follows, so the write
 * happens exactly once regardless of which one the surveyor used to leave.
 */
function LandingInput({
  id,
  value,
  bound,
  disabled,
  onCommit,
}: LandingInputProps): ReactNode {
  // `maxLanding` (in `LandingInputProps` for the caller's convenience) is no
  // longer read here: `min`/`max` attributes are gone along with `type=
  // "number"`, and the ceiling is enforced by `commitRange`, not this input.
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  const commit = (): void => {
    const parsed = Number.parseInt(draft, 10);
    if (Number.isFinite(parsed) && parsed !== value) {
      onCommit(bound === 'fromLanding' ? { fromLanding: parsed } : { toLanding: parsed });
    }
    // Invalid, empty, or unchanged: nothing is written, and the effect above
    // puts the stored value back once `focused` turns false.
  };

  return (
    <input
      id={id}
      className="field__input field__input--mono range__num"
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={draft}
      disabled={disabled}
      onFocus={(event) => {
        // `min`/`max` are gone (the clamp lives in `commitRange`, not here) —
        // a `type="number"` box seeded with the stored digit and never
        // selected is exactly the bug this replaces: tapping a "To" box that
        // reads "4" and typing "3" lands as "43", which `commitRange` clamps
        // straight back to 4, silently. Selecting the draft on focus is what
        // makes a typed digit REPLACE it instead. iOS drops a `select()`
        // called synchronously from the focus handler, so it is deferred to
        // the next animation frame.
        const el = event.currentTarget;
        setDraft(String(value));
        setFocused(true);
        requestAnimationFrame(() => {
          // A fast tap-away (another box focused, or this one blurred) before
          // the frame lands must not steal focus back — `select()` implicitly
          // (re)focuses its element, which would otherwise yank focus off
          // whatever the surveyor moved to next.
          if (document.activeElement === el) el.select();
        });
      }}
      onChange={(event) => setDraft(event.target.value.replace(/\D/g, ''))}
      onBlur={() => {
        commit();
        setFocused(false);
      }}
      onKeyDown={(event) => {
        // Blur only — its own handler commits. Committing here too would be
        // two writes for one Enter.
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
      }}
    />
  );
}
